import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Delete } from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton
} from '@mui/material';
import StyledModal from '../Components/GenericModal/StyledModal';
import { sharedClasses } from '../Components/CustomUIElements/sharedClasses';
import ModalFooterButtons from '../Components/GenericModal/ModalFooterButtons';
import { tableSearchButtonStyles } from '../shared/commonStyles';
import { NumberInput } from '../Components/CustomUIElements/NumberInput/NumberInput';
import { styles } from './styles';

interface Condition {
  id?: number;
  content: string;
  weight: number;
  short_label?: string;
}

interface ConditionsDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  conditions: Condition[];
  onConditionsChange: (conditions: Condition[]) => void;
  onRefineScreening: () => void;
}

const ConditionsDialog: React.FC<ConditionsDialogProps> = ({
  isOpen,
  handleClose,
  conditions,
  onConditionsChange,
  onRefineScreening
}) => {
  const [weightErrors, setWeightErrors] = useState<Record<number, boolean>>({});

  const hasInvalidWeights = () => Object.values(weightErrors).some(Boolean);
  const isSubmitDisabled = conditions.length === 0 || hasInvalidWeights();

  const addCondition = () => {
    onConditionsChange([...conditions, { weight: 1, content: '' }]);
  };

  const removeCondition = (index: number) => {
    const newConditions = [...conditions];
    newConditions.splice(index, 1);
    onConditionsChange(newConditions);
    const newErrors = { ...weightErrors };
    delete newErrors[index];
    setWeightErrors(newErrors);
  };

  const updateWeight = (index: number, value: number) => {
    const newConditions = [...conditions];
    newConditions[index].weight = value;
    onConditionsChange(newConditions);

    const isInvalidWeight = value < 1 || value > 5;
    setWeightErrors((prev) => ({
      ...prev,
      [index]: isInvalidWeight
    }));
  };

  const updateConditionValue = (index: number, field: keyof Condition, value: string | number) => {
    if (field === 'weight') {
      updateWeight(index, Number(value));
      return;
    }
    const newConditions = [...conditions];
    newConditions[index] = {
      ...newConditions[index],
      [field]: value
    };
    onConditionsChange(newConditions);
  };

  const handleRefineScreening = () => {
    onRefineScreening();
    handleClose();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      handleClose={handleClose}
      styleOverrides={{
        maxWidth: '1000px',
        maxHeight: '700px'
      }}
      label="conditions-dialog"
      modalTitle="Edit Conditions"
      subHeaderStyling={false}
    >
      <Box sx={{ ...sharedClasses.mainModalContent, height: '100%' }}>
        <Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '85%' }}>Condition</TableCell>
                  <TableCell style={{ width: '10%' }}>Weight</TableCell>
                  <TableCell style={{ width: '5%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conditions.map((condition, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        value={condition.content}
                        onChange={({ target }) =>
                          updateConditionValue(index, 'content', target.value)
                        }
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={4}
                        sx={styles.noBorderField}
                      />
                    </TableCell>
                    <TableCell>
                      <NumberInput
                        value={condition.weight}
                        onChange={(value) => updateConditionValue(index, 'weight', value)}
                        min={1}
                        max={5}
                        errorText={
                          weightErrors[index] ? 'Weight must be between 1 and 5' : undefined
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => removeCondition(index)} color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3}>
                    <Button variant="contained" onClick={addCondition} sx={tableSearchButtonStyles}>
                      Add Condition
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      <ModalFooterButtons
        primaryButtonText="Refine Screening"
        primaryButtonCallback={handleRefineScreening}
        primaryButtonDisabled={isSubmitDisabled}
        secondaryButtonText="Cancel"
        secondaryButtonCallback={handleClose}
        primaryButtonID="refine-screening-button"
        secondaryButtonID="cancel-conditions"
      />
    </StyledModal>
  );
};

export default ConditionsDialog;
